import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Video as Youtube, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Link2, 
  Image as ImageIcon
} from 'lucide-react';

export const YoutubeDownloader = () => {
  const { withAd, toast } = useAdRedirect();
  const [videoUrl, setVideoUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [isDownloading, setIsDownloading] = useState(''); // track which thumbnail resolution is downloading

  // Parse YouTube Video ID from any input URL
  const extractVideoId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    if (url.trim().length === 11 && !url.includes('/') && !url.includes('.')) {
      return url.trim();
    }
    return '';
  };

  const handleUrlChange = (e) => {
    const input = e.target.value;
    setVideoUrl(input);
    const parsedId = extractVideoId(input);
    setVideoId(parsedId);
  };

  useEffect(() => {
    // Default pleasant demo video ID
    setVideoUrl('https://www.youtube.com/watch?v=jfKfPfyJRdk');
    setVideoId('jfKfPfyJRdk');
  }, []);

  // Thumbnail configurations
  const thumbnailOptions = [
    {
      key: 'maxres',
      name: 'Ultra HD Quality (1080p / 720p)',
      resolution: '1280 x 720',
      badge: 'Maximum Crispness',
      url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      fallbackUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    },
    {
      key: 'sd',
      name: 'Standard Quality (SD)',
      resolution: '640 x 480',
      badge: 'Standard Size',
      url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`
    },
    {
      key: 'hq',
      name: 'Medium Web Quality (HQ)',
      resolution: '480 x 360',
      badge: 'Web Medium',
      url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    },
    {
      key: 'mq',
      name: 'Small Thumbnail Quality (MQ)',
      resolution: '320 x 180',
      badge: 'Small Size',
      url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    }
  ];

  // Secure client-side blob download trigger for thumbnails
  const downloadThumbnail = async (imgUrl, nameLabel) => {
    const filename = `youtube-thumbnail-${videoId}-${nameLabel}.jpg`;
    setIsDownloading(nameLabel);

    try {
      const response = await fetch(imgUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('CORS fetch failure');
      
      const blob = await response.blob();
      const localUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = localUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(localUrl);
    } catch (err) {
      console.warn("Direct blob download blocked by CORS. Redirecting to raw image in a new tab fallback...", err);
      window.open(imgUrl, '_blank');
    } finally {
      setIsDownloading('');
    }
  };

  const handleReset = () => {
    setVideoUrl('');
    setVideoId('');
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free YouTube Thumbnail Downloader - HD & 4K Cover Images" 
        description="Download YouTube video thumbnails in HD, SD, and HQ resolutions. Extract high-quality cover artwork images from any YouTube video URL for free."
        keywords="YouTube thumbnail downloader, download YouTube thumbnail, YouTube video thumbnail, HD thumbnail download, grab youtube cover image"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>YouTube Thumbnail Downloader</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          YouTube Thumbnail Downloader
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Extract and download high-resolution cover artwork and banner thumbnails from any YouTube video instantly.
        </p>
      </div>

      {/* Input section card */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--border-color)',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: 'var(--shadow-sm)',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Link2 size={18} style={{ color: '#ef4444' }} />
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Paste YouTube Video Link</h3>
        </div>

        <div style={{ display: 'flex', gap: '12px' }} className="input-row-downloader">
          <input 
            type="text"
            value={videoUrl}
            onChange={handleUrlChange}
            placeholder="E.G. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
            style={{
              flex: 1,
              padding: '14px 18px',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              fontSize: '1rem',
              fontWeight: 600,
              outline: 'none',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              boxSizing: 'border-box'
            }}
          />
          {videoUrl && (
            <button
              onClick={handleReset}
              style={{
                background: 'rgba(15, 23, 42, 0.03)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '12px',
                padding: '0 20px',
                fontSize: '0.9rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="reset-btn"
            >
              Clear
            </button>
          )}
        </div>

        {videoUrl && !videoId && (
          <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: '#ef4444', fontWeight: 600 }}>
            Could not parse a valid YouTube video ID. Please check your link format.
          </p>
        )}
      </div>

      {/* Extracted Workspace Output */}
      {videoId ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          
          {/* Main big preview */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: 800,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              display: 'block',
              marginBottom: '16px'
            }}>
              Active Video ID: <strong style={{ color: '#ef4444', fontFamily: 'monospace' }}>{videoId}</strong>
            </span>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '24px',
              alignItems: 'center'
            }} className="preview-grid-cover">
              
              <div style={{
                borderRadius: '14px',
                overflow: 'hidden',
                aspectRatio: '16/10',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '680px'
              }}>
                <img 
                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} 
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                  }}
                  alt="YouTube Video Cover" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                  Cover Artwork Ready
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: '420px' }}>
                  Choose your desired thumbnail image dimensions below. For high-resolution visual graphics, we recommend selecting the <strong>Ultra HD Quality</strong> option.
                </p>
                <Link 
                  to="/youtube-video-downloader" 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    marginTop: '8px'
                  }}
                >
                  👉 Looking for YouTube MP4 Video Download? Click here
                </Link>
              </div>

            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '20px' }}>
              Available Thumbnail Resolutions
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px'
            }}>
              {thumbnailOptions.map((opt) => (
                <div
                  key={opt.key}
                  style={{
                    background: '#ffffff',
                    border: '1px solid var(--border-color)',
                    borderRadius: '16px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    boxShadow: 'var(--shadow-xs)',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="resolution-card"
                >
                  <div style={{
                    borderRadius: '10px',
                    overflow: 'hidden',
                    aspectRatio: '16/9',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <img 
                      src={opt.url} 
                      onError={(e) => {
                        if (opt.fallbackUrl) {
                          e.target.src = opt.fallbackUrl;
                        } else {
                          e.target.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
                        }
                      }}
                      alt={opt.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>{opt.name}</span>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      <span>Dimensions: <strong>{opt.resolution}</strong></span>
                      <span style={{
                        background: opt.key === 'maxres' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(15, 23, 42, 0.04)',
                        color: opt.key === 'maxres' ? '#ef4444' : 'var(--text-secondary)',
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {opt.badge}
                      </span>
                    </div>
                  </div>

                  <AdToast message={toast} />
                  <button
                    onClick={withAd(`yt-thumb-${opt.key}`, () => downloadThumbnail(opt.url, opt.key))}
                    disabled={isDownloading === opt.key}
                    style={{
                      marginTop: 'auto',
                      background: opt.key === 'maxres' ? 'linear-gradient(90deg, #ef4444 0%, var(--primary) 100%)' : '#ffffff',
                      color: opt.key === 'maxres' ? '#ffffff' : 'var(--text-primary)',
                      border: opt.key === 'maxres' ? 'none' : '1px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '10px',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className={opt.key === 'maxres' ? 'download-max-btn' : 'download-standard-btn'}
                  >
                    {isDownloading === opt.key ? (
                      <RefreshCw size={14} className="spinner" />
                    ) : (
                      <Download size={14} />
                    )}
                    {isDownloading === opt.key ? 'Downloading...' : 'Download Image'}
                  </button>

                </div>
              ))}
            </div>
          </div>

        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)',
          background: '#ffffff',
          borderRadius: '20px',
          border: '1px solid var(--border-color)'
        }}>
          Please paste a YouTube video link inside the text box above to load thumbnail outputs.
        </div>
      )}

      <style>{`
        @media (min-width: 768px) {
          .preview-grid-cover {
            grid-template-columns: 1.5fr 1fr !important;
          }
        }
        .reset-btn:hover { background: rgba(15, 23, 42, 0.06) !important; }
        .download-max-btn:hover { transform: translateY(-1px); opacity: 0.95; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2) !important; }
        .download-standard-btn:hover { background: var(--bg-secondary) !important; transform: translateY(-1px); }
        .resolution-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-sm) !important; border-color: rgba(239, 68, 68, 0.15) !important; }
        .spinner { animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="youtube-downloader" />
    </div>
  );
};

export default YoutubeDownloader;
